import { useMemo } from 'react'
import { RoundedBox } from '@react-three/drei'
import * as THREE from 'three'
import { giroRodaPorDistancia } from '../../dominio/didatico/rolagemRodas'
import { RodaLiga3D } from '../veiculo/RodaLiga3D'

type Ponto = [number, number, number]
function Barra({ de, para, raio = 0.025, cor = '#8398ab' }: { de: Ponto; para: Ponto; raio?: number; cor?: string }) {
  const a = new THREE.Vector3(...de), b = new THREE.Vector3(...para)
  return <mesh position={a.clone().add(b).multiplyScalar(0.5)} quaternion={new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize())}>
    <cylinderGeometry args={[raio, raio, a.distanceTo(b), 12]} /><meshStandardMaterial color={cor} metalness={0.5} roughness={0.4} />
  </mesh>
}
function Bloco({ posicao, tamanho, cor, opacidade = 1 }: { posicao: Ponto; tamanho: Ponto; cor: string; opacidade?: number }) {
  return <RoundedBox position={posicao} args={tamanho} radius={0.025} smoothness={2}>
    <meshStandardMaterial color={cor} metalness={0.35} roughness={0.38} transparent={opacidade < 1} opacity={opacidade} depthWrite={opacidade === 1} />
  </RoundedBox>
}

/** Carro aberto para ensino: laterais com vãos de roda, cabine e fechamentos translúcidos. */
export function VeiculoImpactos3D({ deformacao, raioX, corImpacto, distanciaRodada = 0, inclinacaoEncosto = 0, variante = 'hatchCompacto' }: { deformacao: number; raioX: boolean; corImpacto: string; distanciaRodada?: number; inclinacaoEncosto?: number; variante?: 'hatchCompacto' | 'sedanMedio' }) {
  const traseira = variante === 'sedanMedio' ? -1.92 : -1.62
  const lateral = useMemo(() => {
    const s = new THREE.Shape()
    s.moveTo(traseira, 0.3); s.lineTo(-1.38, 0.3)
    s.absarc(-1.05, 0.3, 0.33, Math.PI, 0, true)
    s.lineTo(0.72, 0.3); s.absarc(1.05, 0.3, 0.33, Math.PI, 0, true)
    s.lineTo(1.62, 0.3); s.lineTo(1.62, 0.67); s.lineTo(0.65, 0.79)
    s.lineTo(-0.95, 0.84); s.lineTo(traseira, 0.68); s.closePath()
    return s
  }, [traseira])
  return <group name="carro-quatro-impactos-detalhado">
    <Bloco posicao={[0, 0.22, -0.02]} tamanho={[1.18, 0.04, 2.93]} cor="#314453" />
    {[-1, 1].map(lado => <group key={lado}>
      <mesh position={[lado * 0.63, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <extrudeGeometry args={[lateral, { depth: 0.022, bevelEnabled: true, bevelSize: 0.008, bevelThickness: 0.005, bevelSegments: 2, steps: 1, curveSegments: 24 }]} />
        <meshStandardMaterial color="#4c7998" metalness={0.6} roughness={0.33} transparent opacity={raioX ? 0.16 : 0.48} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      {[-1.05, 1.05].map(z => <group key={z}>
        <mesh name="caixa-roda-impactos" position={[lado * 0.63, 0.3, z]} rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.33, 0.022, 8, 32, Math.PI]} /><meshStandardMaterial color="#6e879b" metalness={0.5} roughness={0.45} />
        </mesh>
        <mesh position={[lado * 0.57, 0.3, z]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.337, 0.337, 0.16, 24, 1, true, 0, Math.PI]} /><meshStandardMaterial color="#253846" side={THREE.DoubleSide} roughness={0.8} transparent opacity={0.4} depthWrite={false} />
        </mesh>
        <group name="suspensao-impactos">
          <Barra de={[lado * 0.48, 0.65, z]} para={[lado * 0.54, 0.3, z]} raio={0.018} cor="#a4b5c5" />
          <Barra de={[lado * 0.35, 0.28, z]} para={[lado * 0.66, 0.3, z]} raio={0.022} cor="#73879a" />
          {[0.42, 0.47, 0.52].map(y => <mesh key={y} position={[lado * 0.51, y, z]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.035, 0.006, 5, 12]} /><meshStandardMaterial color="#849aaf" metalness={0.6} roughness={0.5} />
          </mesh>)}
        </group>
        <group position={[lado * 0.66, 0.3, z]} rotation={[0, 0, lado * Math.PI / 2]}><RodaLiga3D raio={0.29} largura={0.18} giro={giroRodaPorDistancia(distanciaRodada, 0.29, lado)} /></group>
      </group>)}
      <Barra de={[lado * 0.57, 0.78, 0.64]} para={[lado * 0.49, 1.65, 0.34]} />
      <Barra de={[lado * 0.49, 1.65, 0.34]} para={[lado * 0.5, 1.64, -0.83]} />
      <Barra de={[lado * 0.5, 1.64, -0.83]} para={[lado * 0.58, 0.8, -1.2]} />
      <Barra de={[lado * 0.56, 0.82, -0.31]} para={[lado * 0.5, 1.65, -0.31]} raio={0.017} />
      <Barra de={[lado * 0.6, 0.34, -0.69]} para={[lado * 0.6, 0.34, 0.69]} raio={0.023} />
      <Bloco posicao={[lado * 0.7, 0.94, 0.48]} tamanho={[0.12, 0.08, 0.17]} cor="#647d91" />
      <Bloco posicao={[lado * 0.42, 0.69, 1.54 - deformacao * 0.15]} tamanho={[0.25, 0.095, 0.04]} cor="#d7eafa" />
      <Bloco posicao={[lado * 0.48, 0.7, traseira + 0.04]} tamanho={[0.13, 0.09, 0.04]} cor="#cf5058" />
    </group>)}
    <Bloco posicao={[0, 0.73, 1.11 - deformacao * 0.12]} tamanho={[1.12, 0.08, 0.85 - deformacao * 0.24]} cor={corImpacto} opacidade={0.52} />
    <Bloco posicao={[0, 0.45, 1.52 - deformacao * 0.2]} tamanho={[1.15, 0.13, 0.10]} cor="#71899a" />
    <Bloco posicao={[0, 0.64, variante === 'sedanMedio' ? -1.46 : -1.31]} tamanho={[1.16, 0.10, variante === 'sedanMedio' ? 0.85 : 0.55]} cor="#4c7998" opacidade={0.4} />
    <Bloco posicao={[0, 1.66, -0.25]} tamanho={[1.04, 0.035, 1.12]} cor="#88a9bf" opacidade={0.065} />
    <Bloco posicao={[0, 0.82, 0.62]} tamanho={[1.08, 0.12, 0.14]} cor="#35495a" opacidade={0.4} />
    <Bloco posicao={[0, 0.46, -0.06]} tamanho={[0.52, 0.12, 0.53]} cor="#23374a" />
    <group name="encosto-banco" position={[0, 0.52, -0.23]} rotation={[inclinacaoEncosto, 0, 0]}>
      <Bloco posicao={[0, 0.39, 0]} tamanho={[0.50, 0.78, 0.10]} cor="#23374a" />
      <Bloco posicao={[0, 0.83, -0.02]} tamanho={[0.27, 0.18, 0.10]} cor="#30495d" />
    </group>
    <Bloco posicao={[0, 0.9, -0.88]} tamanho={[0.94, 0.55, 0.10]} cor="#30495d" />
    <group name="volante-impactos" position={[0, 1.02, 0.49]} rotation={[-0.2, 0, 0]}>
      <mesh><torusGeometry args={[0.205, 0.016, 10, 40]} /><meshStandardMaterial color="#273846" roughness={0.5} /></mesh>
      <Bloco posicao={[0, -0.01, 0]} tamanho={[0.13, 0.075, 0.025]} cor="#344a5c" />
      <Barra de={[-0.19, 0, 0]} para={[-0.04, 0, 0]} raio={0.012} />
      <Barra de={[0.19, 0, 0]} para={[0.04, 0, 0]} raio={0.012} />
      <Barra de={[0, -0.04, 0]} para={[0, -0.195, 0]} raio={0.012} />
    </group>
    <Barra de={[0, 1.02, 0.5]} para={[0, 0.83, 0.7]} raio={0.025} cor="#667f91" />
    {[-0.14, 0.14].map(x => <Bloco key={x} posicao={[x, 0.29, 0.76]} tamanho={[0.10, 0.08, 0.035]} cor="#71879a" />)}
  </group>
}
